import Draw from "ol/interaction/Draw.js";
import Overlay from "ol/Overlay.js";
import Feature from "ol/Feature.js";
import { Point, Circle as CircleGeom, LineString, Polygon } from "ol/geom.js";
import { Vector as VectorSource } from "ol/source.js";
import { Vector as VectorLayer } from "ol/layer.js";
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from "ol/style.js";
import { getArea, getLength } from "ol/sphere.js";
import { unByKey } from "ol/Observable.js";

let draw;
let sketch;
let measureTooltipElement;
let measureTooltip;
const activeDrawings = [];

// Fertig/Abbrechen-Buttons: { finish, cancel } der gerade laufenden
// Zeichnung, oder null wenn keine Messung aktiv ist. Touch hat keine
// Escape-Taste, deshalb brauchen wir eine On-Screen-Alternative.
let activeMeasurement = null;

function setActiveMeasurement(handlers) {
  activeMeasurement = handlers;
  const actions = document.getElementById("measureActions");
  if (actions) actions.classList.toggle("visible", !!handlers);
}

const vectorDrawLayer = new VectorLayer({
  source: new VectorSource(),
  style: {
    "fill-color": "rgba(255,255,255,0.2)",
    "stroke-color": "#ffcc33",
    "stroke-width": 2,
    "circle-radius": 7,
    "circle-fill-color": "#ffcc33",
  },
});
window.vectorDrawLayer = vectorDrawLayer;

/* ==============================
   Gemeinsamer Stil & Hovermarker
============================== */

// Einheitlicher Zeichenstil
function getMeasurementStyle() {
  return new Style({
    fill: new Fill({ color: "rgba(17, 15, 15, 0.2)" }),
    stroke: new Stroke({
      color: "rgba(0,0,0,0.5)",
      lineDash: [10, 10],
      width: 2,
    }),
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: "red" }),
      stroke: new Stroke({ color: "white", width: 2 }),
    }),
  });
}

// Roten Hover-Marker hinzufügen, Rückgabe: {feature, listener}
function addHoverMarker(map, source) {
  const hoverMarker = new Feature(new Point([0, 0]));
  hoverMarker.setStyle(getMeasurementStyle());
  source.addFeature(hoverMarker);

  const moveListener = map.on("pointermove", (evt) => {
    hoverMarker.getGeometry().setCoordinates(evt.coordinate);
  });

  return {
    remove: () => {
      source.removeFeature(hoverMarker);
      unByKey(moveListener);
    },
  };
}

function deactivateActiveMeasurement(map) {
  // Entferne aktive Draw-Interaktionen
  map.getInteractions().forEach((interaction) => {
    if (interaction instanceof Draw) {
      map.removeInteraction(interaction);
    }
  });

  // Entferne laufende Klick-/Move-Listener (z. B. von Radius)
  if (map._currentMeasureListener) {
    unByKey(map._currentMeasureListener);
    map._currentMeasureListener = null;
  }

  setActiveMeasurement(null);
}

// Gemeinsame Fertig/Abbrechen-Logik für Längen-/Flächenmessung (Draw-basiert).
// "Fertig" (Escape-Taste oder Button) schließt die Skizze über OL's
// finishDrawing() ab, was ganz normal "drawend" auslöst — Tooltip und
// measurementResults werden also wie beim Doppelklick-Abschluss befüllt.
// "Abbrechen" verwirft die Skizze über abortDrawing(), ohne etwas zu speichern.
function enableFinishCancelForDraw(map, drawInteraction) {
  let sketchFeature = null;

  const commit = () => {
    if (!sketchFeature) return;
    try {
      drawInteraction.finishDrawing();
    } catch (e) {
      // z. B. zu wenige Stützpunkte für eine gültige Geometrie
      console.warn("Zeichnung konnte nicht abgeschlossen werden:", e);
    }
  };

  const cancel = () => {
    if (!sketchFeature) return;
    drawInteraction.abortDrawing();
  };

  drawInteraction.on("drawstart", (evt) => {
    sketchFeature = evt.feature;
    setActiveMeasurement({ finish: commit, cancel });
  });

  const escHandler = (evt) => {
    if (evt.key === "Escape") commit();
  };
  window.addEventListener("keydown", escHandler);

  const stopListening = () => {
    window.removeEventListener("keydown", escHandler);
    sketchFeature = null;
    setActiveMeasurement(null);
  };

  drawInteraction.once("drawend", stopListening);
  drawInteraction.once("drawabort", stopListening);
}

/* ==============================
   Messsteuerung
============================== */
export function initMeasurementControls(map) {
  const btnRadius = document.getElementById("measurment-Radius-button");
  const btnArea = document.getElementById("measurment-Area-button");
  const btnLength = document.getElementById("measurment-Lenght-button");
  const btnDelete = document.getElementById("measurment-delete-button");
  const btnFinish = document.getElementById("measureFinishButton");
  const btnCancel = document.getElementById("measureCancelButton");

  if (!btnRadius || !btnArea || !btnLength || !btnDelete) return;

  btnRadius.addEventListener("click", () => {
    deactivateActiveMeasurement(map); // ⬅️ beendet vorherigen Modus
    addMeasurementFunctionsRadius(map);
  });

  btnArea.addEventListener("click", () => {
    deactivateActiveMeasurement(map);
    addMeasurementFunctionsArea(map);
  });

  btnLength.addEventListener("click", () => {
    deactivateActiveMeasurement(map);
    addMeasurementFunctionsLength(map);
  });

  btnDelete.addEventListener("click", () => deleteMeasurement(map));

  btnFinish?.addEventListener("click", () => activeMeasurement?.finish());
  btnCancel?.addEventListener("click", () => activeMeasurement?.cancel());
}

/* ==============================
   LÄNGENMESSUNG
   ============================== */
// Array für Messergebnisse (außerhalb der Funktion deklarieren, damit es persistent ist)
let measurementResults = [];

function addMeasurementFunctionsLength(map) {
  const source = new VectorSource();
  if (!map.getLayers().getArray().includes(vectorDrawLayer))
    map.addLayer(vectorDrawLayer);
  const type = "LineString";
  draw = new Draw({ source, type, style: getMeasurementStyle() });
  map.addInteraction(draw);
  enableFinishCancelForDraw(map, draw); // ESC/Fertig-Button beenden, Abbrechen-Button verwirft

  const hover = addHoverMarker(map, source);
  let listener;
  let currentTooltip = null; // Aktuelles Tooltip während des Zeichnens

  draw.on("drawstart", (evt) => {
    sketch = evt.feature;
    hover.remove();

    // Neues Tooltip für diese Messung erstellen
    currentTooltip = createMeasureTooltip(map);
    const { element, overlay } = currentTooltip;

    let tooltipCoord = evt.coordinate;
    listener = sketch.getGeometry().on("change", (evt) => {
      const geom = evt.target;
      const output = formatLength(geom);
      tooltipCoord = geom.getLastCoordinate();
      element.innerHTML = output;
      overlay.setPosition(tooltipCoord);
    });
  });

  draw.on("drawend", (evt) => {
    const feature = evt.feature;
    const geom = feature.getGeometry();
    const length = formatLength(geom);

    // Feature explizit zum vectorDrawLayer hinzufügen, damit es sichtbar bleibt
    const permanentFeature = feature.clone();
    permanentFeature.setStyle(getMeasurementStyle());
    vectorDrawLayer.getSource().addFeature(permanentFeature);

    // Tooltip permanent machen
    const { element, overlay } = currentTooltip;
    element.className = "ol-tooltip ol-tooltip-measure";
    element.innerHTML = length; // Finalen Wert setzen
    overlay.setOffset([0, -7]);
    const finalCoord = geom.getLastCoordinate();
    overlay.setPosition(finalCoord);

    // Tooltip und Overlay dauerhaft speichern, damit es sichtbar bleibt
    activeDrawings.push({ overlay, element });

    // Messergebnis in Array speichern
    measurementResults.push({
      type: "length",
      value: length,
      geometry: geom.clone(),
      coordinates: geom.getCoordinates(),
      timestamp: new Date(),
      feature: permanentFeature,
      overlay: overlay, // Referenz zum Overlay
      element: element, // Referenz zum Tooltip-Element
    });

    sketch = null;
    unByKey(listener);
    currentTooltip = null; // Reset für nächste Messung
  });
}

/* ==============================
   FLÄCHENMESSUNG
============================== */

function addMeasurementFunctionsArea(map) {
  const source = new VectorSource();
  if (!map.getLayers().getArray().includes(vectorDrawLayer))
    map.addLayer(vectorDrawLayer);

  const type = "Polygon";
  draw = new Draw({ source, type, style: getMeasurementStyle() });
  map.addInteraction(draw);
  enableFinishCancelForDraw(map, draw);

  const hover = addHoverMarker(map, source);
  let listener;
  let currentTooltip = null; // Aktuelles Tooltip während des Zeichnens

  draw.on("drawstart", (evt) => {
    sketch = evt.feature;
    hover.remove();

    // Neues Tooltip für diese Messung erstellen
    currentTooltip = createMeasureTooltip(map);
    const { element, overlay } = currentTooltip;

    let tooltipCoord = evt.coordinate;
    listener = sketch.getGeometry().on("change", (evt) => {
      const geom = evt.target;
      const output = formatArea(geom);
      tooltipCoord = geom.getInteriorPoint().getCoordinates();
      element.innerHTML = output;
      overlay.setPosition(tooltipCoord);
    });
  });

  draw.on("drawend", (evt) => {
    const feature = evt.feature;
    const geom = feature.getGeometry();
    const area = formatArea(geom);

    // Feature explizit zum vectorDrawLayer hinzufügen, damit es sichtbar bleibt
    const permanentFeature = feature.clone();
    permanentFeature.setStyle(getMeasurementStyle());
    vectorDrawLayer.getSource().addFeature(permanentFeature);

    // Tooltip permanent machen
    const { element, overlay } = currentTooltip;
    element.className = "ol-tooltip ol-tooltip-measure";
    element.innerHTML = area; // Finalen Wert setzen
    overlay.setOffset([0, -7]);
    const finalCoord = geom.getInteriorPoint().getCoordinates();
    overlay.setPosition(finalCoord);

    // Tooltip und Overlay dauerhaft speichern, damit es sichtbar bleibt
    activeDrawings.push({ overlay, element });

    // Messergebnis in Array speichern
    measurementResults.push({
      type: "area",
      value: area,
      geometry: geom.clone(),
      coordinates: geom.getCoordinates(),
      timestamp: new Date(),
      feature: permanentFeature,
      overlay: overlay, // Referenz zum Overlay
      element: element, // Referenz zum Tooltip-Element
    });

    sketch = null;
    unByKey(listener);
    currentTooltip = null; // Reset für nächste Messung
  });
}

/* ==============================
   RADIUSMESSUNG
============================== */
function addMeasurementFunctionsRadius(map) {
  const source = new VectorSource();
  if (!map.getLayers().getArray().includes(vectorDrawLayer))
    map.addLayer(vectorDrawLayer);

  const layer = new VectorLayer({ source, style: getMeasurementStyle() });
  layer.set("isMeasurementLayer", true);
  map.addLayer(layer);

  const hover = addHoverMarker(map, source);

  let center = null;
  let circleFeature = null;
  let diameterLine = null;
  let pointFeature = null;
  let moveListener = null;
  let currentTooltip = null; // Aktuelles Tooltip für diese Messung
  let currentRadius = 0; // Aktueller Radius-Wert

  const resetRadiusState = () => {
    center = null;
    circleFeature = null;
    diameterLine = null;
    pointFeature = null;
    currentTooltip = null;
    currentRadius = 0;
  };

  // Zweiter Klick oder Fertig-Button → Messung beenden
  const finishRadius = () => {
    if (!center) return; // noch kein Mittelpunkt gesetzt

    if (moveListener) unByKey(moveListener);

    // Features permanent zum vectorDrawLayer hinzufügen
    const permanentCircle = circleFeature.clone();
    permanentCircle.setStyle(getMeasurementStyle());
    vectorDrawLayer.getSource().addFeature(permanentCircle);

    const permanentLine = diameterLine.clone();
    permanentLine.setStyle(getMeasurementStyle());
    vectorDrawLayer.getSource().addFeature(permanentLine);

    const permanentPoint = pointFeature.clone();
    permanentPoint.setStyle(getMeasurementStyle());
    vectorDrawLayer.getSource().addFeature(permanentPoint);

    // Tooltip permanent machen
    const { element, overlay } = currentTooltip;
    const finalRadius = `Radius: ${currentRadius.toFixed(2)} m`;
    const diameter = `Durchmesser: ${(currentRadius * 2).toFixed(2)} m`;
    element.className = "ol-tooltip ol-tooltip-measure";
    element.innerHTML = `${finalRadius}<br>${diameter}`;
    overlay.setOffset([0, -7]);
    overlay.setPosition(center);

    // Tooltip und Overlay dauerhaft speichern
    activeDrawings.push({ overlay, element });

    // Messergebnis in Array speichern
    measurementResults.push({
      type: "radius",
      radius: currentRadius,
      diameter: currentRadius * 2,
      value: finalRadius, // Formatierter Wert
      center: center,
      geometry: circleFeature.getGeometry().clone(),
      timestamp: new Date(),
      features: {
        circle: permanentCircle,
        line: permanentLine,
        point: permanentPoint,
      },
      overlay: overlay,
      element: element,
    });

    resetRadiusState();
    setActiveMeasurement(null);
  };

  // Abbrechen-Button → Skizze verwerfen, ohne ein Ergebnis zu speichern
  const cancelRadius = () => {
    if (!center) return; // noch kein Mittelpunkt gesetzt

    if (moveListener) unByKey(moveListener);

    [pointFeature, circleFeature, diameterLine].forEach((f) => {
      if (f) source.removeFeature(f);
    });
    if (currentTooltip) {
      map.removeOverlay(currentTooltip.overlay);
      currentTooltip.element.remove();
    }

    resetRadiusState();
    setActiveMeasurement(null);
  };

  map._currentMeasureListener = map.on("click", (evt) => {
    if (!center) {
      // Erster Klick → Mittelpunkt festlegen
      hover.remove();
      center = evt.coordinate;

      // Neues Tooltip für diese Messung erstellen
      currentTooltip = createMeasureTooltip(map);
      const { element, overlay } = currentTooltip;

      pointFeature = new Feature(new Point(center));
      source.addFeature(pointFeature);

      circleFeature = new Feature(new CircleGeom(center, 1));
      source.addFeature(circleFeature);

      diameterLine = new Feature(new LineString([center, center]));
      source.addFeature(diameterLine);

      setActiveMeasurement({ finish: finishRadius, cancel: cancelRadius });

      moveListener = map.on("pointermove", (moveEvt) => {
        const r = getDistance(center, moveEvt.coordinate);
        currentRadius = r; // Radius speichern
        circleFeature.getGeometry().setRadius(r);

        const dx = moveEvt.coordinate[0] - center[0];
        const dy = moveEvt.coordinate[1] - center[1];
        const angle = Math.atan2(dy, dx);

        const x1 = center[0] - r * Math.cos(angle);
        const y1 = center[1] - r * Math.sin(angle);
        const x2 = center[0] + r * Math.cos(angle);
        const y2 = center[1] + r * Math.sin(angle);
        diameterLine.getGeometry().setCoordinates([
          [x1, y1],
          [x2, y2],
        ]);

        // Tooltip in der Mitte des Kreises anzeigen
        element.innerHTML = `Radius: ${r.toFixed(2)} m`;
        overlay.setPosition(center);
      });
    } else {
      // Zweiter Klick → Messung beenden
      finishRadius();
    }
  });
}

/* ==============================
   Hilfsfunktionen
============================== */
function createMeasureTooltip(map) {
  const element = document.createElement("div");
  element.className = "ol-tooltip ol-tooltip-measure";
  const overlay = new Overlay({
    element,
    offset: [0, -15],
    positioning: "bottom-center",
  });
  map.addOverlay(overlay);
  // ❌ Kein activeDrawings.push() hier!
  return { element, overlay };
}

function finalizeDraw(map, listener) {
  measureTooltipElement.className = "ol-tooltip ol-tooltip-measure"; // ⚡️ bleib bei weiß/grau
  measureTooltip.setOffset([0, -7]);
  sketch = null;
  unByKey(listener);
}

function formatLength(line) {
  const length = getLength(line);
  return length > 100
    ? Math.round((length / 1000) * 100) / 100 + " km"
    : Math.round(length * 100) / 100 + " m";
}

function formatArea(polygon) {
  const area = getArea(polygon);
  return area > 10000
    ? Math.round((area / 1000000) * 100) / 100 + " km²"
    : Math.round(area * 100) / 100 + " m²";
}

function getDistance(a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}

function deleteMeasurement(map) {
  // Interaktionen beenden
  map.getInteractions().forEach((i) => {
    if (i instanceof Draw) map.removeInteraction(i);
  });

  // Alle Mess-Layer löschen
  map.getLayers().forEach((l) => {
    if (l.get("isMeasurementLayer") === true) {
      l.getSource()?.clear();
      map.removeLayer(l);
    }
    if (l === window.vectorDrawLayer) l.getSource()?.clear();
  });

  // ✅ Tooltips zuverlässig löschen
  activeDrawings.forEach(({ overlay, element }) => {
    try {
      if (overlay && map.getOverlays().getArray().includes(overlay)) {
        map.removeOverlay(overlay);
      }
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    } catch (e) {
      console.warn("Fehler beim Entfernen eines Overlays:", e);
    }
  });

  // ✅ Auch alle verbliebenen Tooltip-Container im DOM direkt löschen
  document
    .querySelectorAll(".ol-tooltip.ol-tooltip-measure")
    .forEach((el) => el.remove());

  // Cleanup: Liste & Listener zurücksetzen
  activeDrawings.length = 0;

  if (map._currentMeasureListener) {
    unByKey(map._currentMeasureListener);
    map._currentMeasureListener = null;
  }

  setActiveMeasurement(null);
}

// Optional: Funktion zum Abrufen aller Messergebnisse
function getMeasurementResults() {
  return measurementResults;
}

// Optional: Funktion zum Löschen aller Messungen
function clearMeasurements() {
  // Tooltips entfernen
  measurementResults.forEach((measurement) => {
    if (measurement.overlay) {
      measurement.overlay.setPosition(undefined);
    }
  });
  // Features vom Layer entfernen
  measurementResults.forEach((measurement) => {
    if (measurement.feature) {
      vectorDrawLayer.getSource().removeFeature(measurement.feature);
    }
  });
  measurementResults = [];
}
